import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../components/feature/Navbar';
import Footer from '../components/feature/Footer';

export default function NotFound() {
  const location = useLocation();
  const [glitch, setGlitch] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Random glitch flicker effect
  useEffect(() => {
    const trigger = () => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 200);
    };
    const interval = setInterval(trigger, 3000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar scrolled={scrolled} />

      <main className="flex-1 relative flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/69fabf1b36d76ab65d4613ae3ac98abf.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />

        {/* Drip effect at top */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-red-700/60 to-transparent" />

        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto py-24">
          {/* Giant 404 */}
          <div className="relative mb-4 select-none">
            <span
              className={`block text-[10rem] md:text-[14rem] font-black font-['Cinzel'] leading-none transition-all duration-75 ${
                glitch
                  ? 'text-white [text-shadow:3px_0_0_#dc2626,-3px_0_0_#7f1d1d]'
                  : 'text-red-700/30'
              }`}
            >
              404
            </span>
            {/* Overlay solid number */}
            <span
              className={`absolute inset-0 block text-[10rem] md:text-[14rem] font-black font-['Cinzel'] leading-none text-red-600 transition-all duration-75 ${
                glitch ? 'translate-x-1 opacity-60' : 'translate-x-0 opacity-100'
              }`}
            >
              404
            </span>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-20 bg-red-800/50" />
            <i className="ri-skull-line text-red-600 text-xl" />
            <div className="h-px w-20 bg-red-800/50" />
          </div>

          {/* Headline */}
          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Cinzel'] mb-4 tracking-wide">
            File Lost to the Darkness
          </h1>

          <p className="text-gray-400 font-['Crimson_Text'] text-lg md:text-xl leading-relaxed mb-3">
            Whatever you were searching for has been swallowed by the void.
            The path{' '}
            <span className="text-red-500 font-semibold font-mono text-base">
              {location.pathname}
            </span>{' '}
            does not exist, or perhaps it never did.
          </p>

          <p className="text-gray-600 font-['Crimson_Text'] text-base italic mb-10">
            "Not all who wander are lost — but you certainly are."
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 font-['Crimson_Text'] text-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line mr-2" />
              Return Home
            </Link>
            <Link
              to="/stories"
              className="px-8 py-3 bg-[#1a1a1a] hover:bg-[#222] border border-red-900/30 hover:border-red-600/50 text-gray-300 hover:text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 font-['Crimson_Text'] text-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line mr-2" />
              Browse Stories
            </Link>
          </div>

          {/* Quick nav links */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {[
              { label: 'Top Ranked', to: '/top-ranked' },
              { label: 'Writers', to: '/writers' },
              { label: 'Community', to: '/community' },
              { label: 'About', to: '/about' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-gray-500 hover:text-red-500 transition-colors font-['Crimson_Text'] text-sm cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes glitch-shift {
          0%   { clip-path: inset(0 0 90% 0); transform: translateX(-4px); }
          20%  { clip-path: inset(40% 0 50% 0); transform: translateX(4px); }
          40%  { clip-path: inset(70% 0 10% 0); transform: translateX(-2px); }
          60%  { clip-path: inset(20% 0 70% 0); transform: translateX(3px); }
          80%  { clip-path: inset(60% 0 30% 0); transform: translateX(-3px); }
          100% { clip-path: inset(0 0 90% 0); transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
